import NodeViewConstructorArgs from '../constructor/NodeViewConstructorArgs';
import AbstractNodeView from './AbstractNodeView';
import * as d3 from 'd3';
import eventIcon from '../assets/event.svg';
import eventDisable from '../assets/event_disable.svg';
import WorkFlowView from '../view/WorkFlowView';
import View from '../view/View';
import { Syntax } from '@trlogic/ui-pack';
import { TransactionJsonModel } from '../../../../domain/stream/transaction/TransactionDomains';
import { NodeType } from '../../../../domain/collection/rule/FlowDomains';
import '../tree.css';
import WorkflowLayoutUtil from '../../../../util/WorkflowLayoutUtil';

class EventNodeView extends AbstractNodeView {
  private mainNode: d3.Selection<any, any, any, any> | undefined;
  private menuOpen: boolean = false;

  public constructor(args: NodeViewConstructorArgs) {
    super(args);
  }

  public initialize(): void {
    this.render();
  }

  public render(): void {
    let node: any;
    if (this.shadow) {
      node = d3.select((this.workFlowView as WorkFlowView).getParent()).append('svg');
    } else {
      node = d3
        .select((this.workFlowView as WorkFlowView).getNode())
        .select('#workflow-container-group')
        .append('g');
    }
    let text = this.getModel<any>().value.name == '' ? 'Select a transaction' : this.getModel<any>().value.name;
    let displayedText = text.length > 20 ? text.substr(0, 20) + '...' : text;

    this.mainNode = node
      .attr('id', this.el.substring(1, this.el.length))
      .attr('type', 'Event')
      .style('position', 'absolute')
      .style('pointer-events', 'auto')
      .style('width', '50px')
      .style('height', '50px')
      .style('cursor', 'pointer');

    this.mainNode
      ?.append('rect')
      .attr('width', 50)
      .attr('height', 50)
      .attr('rx', 3)
      .style('stroke', 'black')
      .attr('stroke-width', 1)
      .style('fill', 'white')
      .style('stroke-dasharray', '0');

    this.mainNode
      ?.append('svg:image')
      .attr('xlink:href', eventIcon)
      .attr('width', 20)
      .attr('height', 20)
      .attr('x', 50 / 2 - 10)
      .attr('y', 50 / 2 - 10);

    this.mainNode
      ?.append('text')
      .attr('x', 50 / 2 + 40)
      .attr('y', 50 / 2 - 2)
      .attr('dy', '.35em')
      .style('fill', '#4F4F4F')
      .text(displayedText);

    const status = this.getModel<any>().value.status;
    if (status === false) {
      d3.selectAll(this.getNode()).select('rect').style('stroke', '#bdbdbd').style('stroke-dasharray', '6');
      d3.selectAll(this.getNode())
        .select('image')
        .attr('xlink:href', eventDisable)
        .attr('width', 20)
        .attr('height', 20)
        .attr('x', 50 / 2 - 10)
        .attr('y', 50 / 2 - 10);
    }
  }

  public socketHighlight(type?: NodeType) {
    if (!this.isShadow() && type == NodeType.EVENT) {
      d3.select(this.getNode()).select('rect').style('stroke', '#2F9BFF').style('stroke-dasharray', '6');
    }
  }

  public socketUnHighlight(type?: NodeType): void {
    if (!this.isShadow() && type == NodeType.EVENT) {
      d3.selectAll(this.getNode()).select('rect').style('stroke', 'black').style('stroke-dasharray', '0');
      const socketRect = this.getSocketBoundingRect();
      this.createDropdownMenu(socketRect);
    }
  }

  private getSocketBoundingRect() {
    const socketElement: any = d3.select(this.getNode()).select('rect').node();
    return socketElement.getBoundingClientRect();
  }

  private createDropdownMenu(socketRect: any) {
    if (this.menuOpen) return;
    this.menuOpen = true;
    this.workFlowView.setMenuOpen(true);
    const viewportHeight = window.innerHeight;
    const menuHeight = viewportHeight * 0.25;

    const left = socketRect.right + 20;
    const top = WorkflowLayoutUtil.calculateDropdownTop(socketRect.top, menuHeight, undefined, viewportHeight);

    const menuContainer = d3
      .select('body')
      .append('ul')
      .attr('id', 'transaction-menu')
      .attr('class', 'transaction-menu')
      .style('position', 'absolute')
      .style('left', left + 'px')
      .style('top', top + 'px')
      .style('background-color', 'white')
      .style('width', '250px')
      .style('height', '25%')
      .style('border', '1px solid #E0E4E8')
      .style('border-radius', '4px')
      .style('overflow-y', 'auto')
      .style('overflow-x', 'hidden');

    const header = menuContainer.append('div').style('background-color', '#F9FAFB').style('border-bottom', '1px solid #E0E4E8').style('padding', '8px');

    header
      .append('input')
      .attr('type', 'text')
      .attr('placeholder', 'Search')
      .style('width', '100%')
      .style('border', '1px solid #E0E4E8')
      .style('border-radius', '4px')
      .style('padding', '8px')
      .on('input', function () {
        const searchText = this.value.trim().toLowerCase();
        menuContainer.selectAll('li').style('display', function () {
          const itemText = d3.select(this).text().toLowerCase();
          return itemText.includes(searchText) ? 'flex' : 'none';
        });
      });

    menuContainer.selectAll('li').style('padding', '10px').style('display', 'flex').style('align-items', 'center');

    (this.workFlowView as any).eventItems.map((event: TransactionJsonModel) => {
      const listItem = menuContainer
        .append('li')
        .attr('data-testid', `select-item-${event.name}`)
        .style('display', 'flex')
        .style('font-size', '15px')
        .style('padding', '8px')
        .style('align-items', 'center');

      const svgImage = listItem.append('svg').attr('width', 16).attr('height', 16).style('min-width', '20px').style('margin-left', '6px');

      svgImage.append('svg:image').attr('xlink:href', eventIcon).attr('width', 16).attr('height', 16);

      listItem.append('span').style('margin-left', '6px').style('color', '#4F4F4F').style('overflow-x', 'auto').text(event.name);

      listItem
        .on('mouseover', () => {
          listItem.style('background-color', '#F0F0F0').style('cursor', 'pointer');
        })
        .on('mouseout', () => {
          listItem.style('background-color', 'transparent').style('cursor', 'default');
        });

      listItem.on('click', () => {
        this.workFlowView.addEventItem(event.name);
        this.closeDropdownMenu();
      });
    });
    d3.select(document).on('pointerup', this.handleOutsideClick);
  }

  private closeDropdownMenu() {
    d3.select('#transaction-menu').remove();
    d3.select(document).on('pointerup', null);
    this.menuOpen = false;
    this.workFlowView.setMenuOpen(false);
    this.workFlowView.reRender();
  }

  private handleOutsideClick = (event: any) => {
    const menuContainer: any = d3.select('#transaction-menu');
    if (this.menuOpen && menuContainer && !menuContainer.node().contains(event.target)) {
      this.closeDropdownMenu();
    }
  };

  protected preSwap(target: View<Syntax>): boolean {
    switch (target.constructor) {
      case EventNodeView:
        return true;
      default:
        return false;
    }
  }

  public setModel(model: Syntax) {}
}

export default EventNodeView;
